import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectAdminToken } from '../../admin/sessionSlice';
import { s3PublicUrl } from '../../utils/s3PublicUrl';
import {
  completeStageRevealNavigation,
  isDemoSubscribeRedirectUser,
  runDemoStageSubmitFlow,
  scheduleStagePostSubmitNavigation,
} from '../../utils/demoGate';
import { mergeGameAssignmentIntoUserData } from '../../utils/gameStages';
import { buildCumulativeStageTimes, buildCumulativeStageScore, readCumulativeScoreFromProgress, readCumulativeTimeFromProgress } from '../../utils/stageReportTime';
import { resolvePostSubmitNavigation } from '../../utils/stageNavigation';
import {
  fetchGameStages,
  initStageReport,
  updateStageProgress,
} from '../../functions/stageReport';
import { sendReport } from '../../functions/sendReport';
import { formatGameTimer, resolveCountdownElapsedSeconds } from '../../utils/gameTimer';
import { buildStagePageStyle, stageThemeVisuals } from '../../utils/stageThemeVisuals';
import {
  StagePlayGate,
  useStageAlreadyPlayedGuard,
} from '../../utils/useStageAlreadyPlayedGuard';
import { useStageBackButton } from '../../utils/useStageBackButton';
import { getStageRulesList, useStageRulesModal } from '../../utils/useStageRulesModal';
import StagePuzzleReveal from '../../components/StagePuzzleReveal/StagePuzzleReveal';
import '../../components/StagePuzzleReveal/stageLayout.css';
import './wordle.css';

const STAGE_NUMBER = 5;

const MEMORIZE_SECONDS = 30;
const GUESS_SECONDS = 180;
const RESPONSE_MAX = 500;
const MAX_WORDS = 50;
const WORD_CHAR_MAX = 50;
const STAGE1_WORDS_SEEN_PREFIX = 'stage1_words_seen';
const STAGE1_MEMORIZE_DEADLINE_PREFIX = 'stage1_memorize_deadline';
const STAGE1_GUESS_DEADLINE_PREFIX = 'stage1_guess_deadline';

const WORD_CARD_PALETTE = [
  { from: '#F8A81D', to: '#F8A81D', text: '#072652' },
  { from: '#15539E', to: '#072652', text: '#ffffff' },
  { from: '#072652', to: '#072652', text: '#ffffff' },
];

function wordCardStyle(index) {
  const { from, to, text } = WORD_CARD_PALETTE[index % WORD_CARD_PALETTE.length];
  return {
    background: `linear-gradient(180deg, ${from} 0%, ${to} 100%)`,
    '--word-card-text': text,
  };
}

function formatTimer(totalSeconds) {
  return formatGameTimer(totalSeconds);
}

function ClockIcon() {
  return (
    <svg className="s1-timer-icon" viewBox="0 0 24 24" aria-hidden>
      <path
        fill="currentColor"
        d="M12 2a10 10 0 1 0 10 10A10.011 10.011 0 0 0 12 2Zm0 18a8 8 0 1 1 8-8 8.009 8.009 0 0 1-8 8Zm.5-13H11v6l4.7 2.8.8-1.3-4-2.4Z"
      />
    </svg>
  );
}

function stage1WordsSeenKey(userId, sessionId) {
  return `${STAGE1_WORDS_SEEN_PREFIX}_${userId}_${sessionId}`;
}

function stage1MemorizeDeadlineKey(userId, sessionId) {
  return `${STAGE1_MEMORIZE_DEADLINE_PREFIX}_${userId}_${sessionId}`;
}

function stage1GuessDeadlineKey(userId, sessionId) {
  return `${STAGE1_GUESS_DEADLINE_PREFIX}_${userId}_${sessionId}`;
}

function readMemorizeStorage(user) {
  const userId = user?.userId;
  const sessionId = user?.sessionId;
  if (!userId || !sessionId) {
    return { done: false, deadline: 0 };
  }
  try {
    const done = sessionStorage.getItem(stage1WordsSeenKey(userId, sessionId)) === '1';
    const deadline = Number(sessionStorage.getItem(stage1MemorizeDeadlineKey(userId, sessionId)) || 0);
    return { done, deadline: Number.isFinite(deadline) ? deadline : 0 };
  } catch {
    return { done: false, deadline: 0 };
  }
}

function markStage1WordsDone(user) {
  const userId = user?.userId;
  const sessionId = user?.sessionId;
  if (!userId || !sessionId) return;
  try {
    sessionStorage.setItem(stage1WordsSeenKey(userId, sessionId), '1');
    sessionStorage.removeItem(stage1MemorizeDeadlineKey(userId, sessionId));
  } catch {
    /* ignore */
  }
}

function readGuessDeadline(user) {
  const userId = user?.userId;
  const sessionId = user?.sessionId;
  if (!userId || !sessionId) return 0;
  try {
    const deadline = Number(sessionStorage.getItem(stage1GuessDeadlineKey(userId, sessionId)) || 0);
    return Number.isFinite(deadline) ? deadline : 0;
  } catch {
    return 0;
  }
}

function setGuessDeadline(user, deadlineMs) {
  const userId = user?.userId;
  const sessionId = user?.sessionId;
  if (!userId || !sessionId) return;
  try {
    sessionStorage.setItem(stage1GuessDeadlineKey(userId, sessionId), String(deadlineMs));
  } catch {
    /* ignore */
  }
}

function clearGuessDeadline(user) {
  const userId = user?.userId;
  const sessionId = user?.sessionId;
  if (!userId || !sessionId) return;
  try {
    sessionStorage.removeItem(stage1GuessDeadlineKey(userId, sessionId));
  } catch {
    /* ignore */
  }
}

function setMemorizeDeadline(user, deadlineMs) {
  const userId = user?.userId;
  const sessionId = user?.sessionId;
  if (!userId || !sessionId) return;
  try {
    sessionStorage.setItem(stage1MemorizeDeadlineKey(userId, sessionId), String(deadlineMs));
  } catch {
    /* ignore */
  }
}

function isPageReload() {
  try {
    const nav = performance.getEntriesByType('navigation')[0];
    return nav?.type === 'reload';
  } catch {
    return false;
  }
}

function clearIntervalTimer(timerRef) {
  if (timerRef.current) {
    window.clearInterval(timerRef.current);
    timerRef.current = null;
  }
}

function normalizeWord(raw) {
  const word = String(raw ?? '')
    .replace(/[^a-zA-Z\s-]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toUpperCase()
    .slice(0, WORD_CHAR_MAX);
  if (!word) return '';
  return word;
}

function normalizeWordType(raw) {
  const val = String(raw ?? '').trim().toLowerCase();
  return val === 'bad' ? 'bad' : 'good';
}

function normalizeWordEntries(questions) {
  const seen = new Set();
  const out = [];
  for (const q of Array.isArray(questions) ? questions : []) {
    const word = normalizeWord(q?.word);
    if (!word || seen.has(word)) continue;
    seen.add(word);
    out.push({
      word,
      type: normalizeWordType(q?.type ?? q?.kind ?? q?.word_type ?? q?.wordType ?? ''),
    });
    if (out.length >= MAX_WORDS) break;
  }
  return out;
}

function parseGuessWords(text) {
  const normalized = normalizeWord(text);
  return normalized ? [normalized] : [];
}

function countCorrectWords(guesses, correctWords) {
  const correctSet = new Set(correctWords);
  const matched = new Set();
  let count = 0;
  for (const guess of guesses) {
    if (correctSet.has(guess) && !matched.has(guess)) {
      matched.add(guess);
      count += 1;
    }
  }
  return count;
}

export default function Stage1({ onStageComplete }) {
  const navigate = useNavigate();
  useStageBackButton(1);
  const themeData = useSelector((state) => state.theme.data);
  const user = useSelector((state) => state.auth?.user);
  const adminToken = useSelector(selectAdminToken);
  const stage1 = themeData?.stages_data?.stage_5;

  const logoUrl = s3PublicUrl(stage1?.logo ?? stage1?.badge);
  const rulesList = getStageRulesList(stage1);
  const subtitle =
    String(
      stage1?.title ?? stage1?.rules_title ?? stage1?.name ?? 'Remember it... Before it remembers you!'
    ).trim() || 'Remember it... Before it remembers you!';

  const wordEntries = useMemo(() => normalizeWordEntries(stage1?.questions), [stage1?.questions]);
  const wordList = useMemo(() => wordEntries.map((entry) => entry.word), [wordEntries]);
  const goodWords = useMemo(
    () => wordEntries.filter((entry) => entry.type === 'good').map((entry) => entry.word),
    [wordEntries]
  );
  const badWordSet = useMemo(
    () => new Set(wordEntries.filter((entry) => entry.type === 'bad').map((entry) => entry.word)),
    [wordEntries]
  );

  const visuals = useMemo(() => stageThemeVisuals(themeData), [themeData]);

  const pageStyle = useMemo(() => buildStagePageStyle(visuals, 's1'), [visuals]);

  const isConfigured =
    !!stage1 && String(stage1?.title ?? '').trim().length > 0 && wordList.length > 0;

  const completeSentRef = useRef(false);
  const timerRef = useRef(null);
  const guessTimerRef = useRef(null);
  const autoSubmitRef = useRef(false);
  const handleSubmitRef = useRef(null);
  const revealTimeoutRef = useRef(null);
  const nextPathRef = useRef('/puzzle');
  const guessInputRef = useRef(null);

  const [guessInput, setGuessInput] = useState('');
  const [revealedWords, setRevealedWords] = useState([]);
  const [message, setMessage] = useState('');
  const [inputShake, setInputShake] = useState(false);
  const [inputWrong, setInputWrong] = useState(false);
  const [logoFailed, setLogoFailed] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showRevealOverlay, setShowRevealOverlay] = useState(false);
  const [revealCompletedDays, setRevealCompletedDays] = useState(null);
  const [wordsRevealed, setWordsRevealed] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [guessSecondsLeft, setGuessSecondsLeft] = useState(0);
  const [memorizeReady, setMemorizeReady] = useState(false);
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
  const [showMemorizeIntro, setShowMemorizeIntro] = useState(false);
  const [memorizeStartConfirmed, setMemorizeStartConfirmed] = useState(false);
  const [showBadWordModal, setShowBadWordModal] = useState(false);
  const [badWordGuess, setBadWordGuess] = useState('');

  const { playCheckReady, alreadyPlayed, beginSubmit } = useStageAlreadyPlayedGuard({
    stageNumber: STAGE_NUMBER,
    submitted,
    user,
    adminToken,
    navigate,
  });

  const skipRulesAutoOpen = useMemo(() => {
    if (!user?.userId || !user?.sessionId) return false;
    return readMemorizeStorage(user).done;
  }, [user?.userId, user?.sessionId]);

  const stageRulesModal = useStageRulesModal({
    stageNumber: STAGE_NUMBER,
    rulesList,
    isConfigured,
    submitted,
    alreadyPlayed,
    userId: user?.userId,
    sessionId: user?.sessionId,
    ready: !!stage1,
    skipAutoOpen: skipRulesAutoOpen,
  });
  const { showRules, openRules, closeRules } = stageRulesModal;
  const rulesDismissed = stageRulesModal.rulesDismissed;

  const handleOpenRulesClick = () => {
    openRules();
  };

  const revealedWordSet = useMemo(() => new Set(revealedWords), [revealedWords]);

  const phaseTimerLabel = wordsRevealed ? 'Remember' : 'Guess';
  const phaseTimerDisplay = wordsRevealed
    ? formatTimer(secondsLeft)
    : formatTimer(guessSecondsLeft);

  useEffect(() => {
    setLogoFailed(false);
  }, [logoUrl]);

  useEffect(
    () => () => {
      if (revealTimeoutRef.current) {
        window.clearTimeout(revealTimeoutRef.current);
        revealTimeoutRef.current = null;
      }
    },
    []
  );

  useEffect(() => {
    if (
      !isConfigured ||
      submitted ||
      alreadyPlayed ||
      !playCheckReady ||
      !rulesDismissed ||
      showRules ||
      memorizeStartConfirmed
    ) {
      return undefined;
    }

    const userId = user?.userId;
    const sessionId = user?.sessionId;
    if (!userId || !sessionId) return undefined;

    const { done, deadline } = readMemorizeStorage(user);
    if (done || deadline > 0) return undefined;

    setMemorizeReady(true);
    setShowMemorizeIntro(true);
    return undefined;
  }, [
    alreadyPlayed,
    isConfigured,
    memorizeStartConfirmed,
    playCheckReady,
    rulesDismissed,
    showRules,
    submitted,
    user?.userId,
    user?.sessionId,
  ]);

  useEffect(() => {
    if (!isConfigured || submitted || alreadyPlayed || !playCheckReady || !rulesDismissed) {
      return undefined;
    }

    const userId = user?.userId;
    const sessionId = user?.sessionId;
    if (!userId || !sessionId) return undefined;

    const { done, deadline } = readMemorizeStorage(user);
    const now = Date.now();

    clearIntervalTimer(timerRef);

    const finishMemorize = () => {
      markStage1WordsDone(user);
      setWordsRevealed(false);
      setSecondsLeft(0);
      clearIntervalTimer(timerRef);
    };

    const startCountdown = (remainingSeconds) => {
      const safeRemaining = Math.max(0, Math.ceil(Number(remainingSeconds) || 0));
      setMemorizeReady(true);
      setWordsRevealed(safeRemaining > 0);
      setSecondsLeft(safeRemaining);

      if (safeRemaining <= 0) {
        finishMemorize();
        return;
      }

      timerRef.current = window.setInterval(() => {
        setSecondsLeft((prev) => {
          if (prev <= 1) {
            finishMemorize();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    };

    if (done) {
      setMemorizeReady(true);
      setWordsRevealed(false);
      setSecondsLeft(0);
      return undefined;
    }

    if (deadline > 0) {
      setMemorizeReady(true);
      if (isPageReload() || now >= deadline) {
        finishMemorize();
        return undefined;
      }
      startCountdown((deadline - now) / 1000);
      return () => clearIntervalTimer(timerRef);
    }

    if (!memorizeStartConfirmed) {
      return undefined;
    }

    const endAt = now + MEMORIZE_SECONDS * 1000;
    setMemorizeDeadline(user, endAt);
    startCountdown(MEMORIZE_SECONDS);

    return () => clearIntervalTimer(timerRef);
  }, [
    alreadyPlayed,
    isConfigured,
    memorizeStartConfirmed,
    playCheckReady,
    rulesDismissed,
    submitted,
    user?.userId,
    user?.sessionId,
  ]);

  useEffect(() => {
    if (!isConfigured || submitted || alreadyPlayed || !playCheckReady || !rulesDismissed) {
      return undefined;
    }
    if (!memorizeReady || wordsRevealed) {
      return undefined;
    }

    const userId = user?.userId;
    const sessionId = user?.sessionId;
    if (!userId || !sessionId) return undefined;

    clearIntervalTimer(guessTimerRef);

    const finishGuessTime = () => {
      clearIntervalTimer(guessTimerRef);
      setGuessSecondsLeft(0);
      clearGuessDeadline(user);
      if (!autoSubmitRef.current) {
        autoSubmitRef.current = true;
        handleSubmitRef.current?.(null, { auto: true });
      }
    };

    const startGuessCountdown = (remainingSeconds) => {
      const safeRemaining = Math.max(0, Math.ceil(Number(remainingSeconds) || 0));
      setGuessSecondsLeft(safeRemaining);

      if (safeRemaining <= 0) {
        finishGuessTime();
        return;
      }

      let remaining = safeRemaining;
      guessTimerRef.current = window.setInterval(() => {
        remaining -= 1;
        const next = Math.max(0, remaining);
        setGuessSecondsLeft(next);
        if (next <= 0) {
          finishGuessTime();
        }
      }, 1000);
    };

    const now = Date.now();
    let guessDeadline = readGuessDeadline(user);
    if (!guessDeadline) {
      guessDeadline = now + GUESS_SECONDS * 1000;
      setGuessDeadline(user, guessDeadline);
    }

    if (now >= guessDeadline) {
      finishGuessTime();
      return undefined;
    }

    startGuessCountdown((guessDeadline - now) / 1000);

    return () => clearIntervalTimer(guessTimerRef);
  }, [
    alreadyPlayed,
    isConfigured,
    memorizeReady,
    playCheckReady,
    rulesDismissed,
    submitted,
    user?.userId,
    user?.sessionId,
    wordsRevealed,
  ]);

  const triggerShake = () => {
    setInputShake(true);
    window.setTimeout(() => setInputShake(false), 500);
  };

  const handleWordGuess = () => {
    if (submitted) return;
    const guesses = parseGuessWords(guessInput);
    const guess = guesses[0] || '';
    if (!guess) {
      triggerShake();
      setMessage('Please type one word to check.');
      return;
    }
    if (guesses.length > 1) {
      setMessage('Enter one word at a time and click OK.');
      return;
    }
    if (revealedWordSet.has(guess)) {
      setGuessInput('');
      setMessage('You already revealed that word.');
      return;
    }
    if (badWordSet.has(guess)) {
      setInputWrong(true);
      triggerShake();
      setBadWordGuess(guess);
      setShowBadWordModal(true);
      setMessage('');
      window.requestAnimationFrame(() => {
        const input = guessInputRef.current;
        if (input) {
          input.focus();
          input.select();
        }
      });
      return;
    }
    if (!goodWords.includes(guess)) {
      setInputWrong(true);
      triggerShake();
      setMessage('No match. Try again.');
      window.requestAnimationFrame(() => {
        const input = guessInputRef.current;
        if (input) {
          input.focus();
          input.select();
        }
      });
      return;
    }
    setInputWrong(false);
    setRevealedWords((prev) => [...prev, guess]);
    setGuessInput('');
    setMessage('Nice! The matching word has flipped.');
  };

  const handleMemorizeIntroConfirm = () => {
    setShowMemorizeIntro(false);
    setMemorizeStartConfirmed(true);
  };

  const handleSubmitClick = () => {
    if (submitted) return;
    if (wordsRevealed) {
      setMessage('Wait for the words to hide before submitting.');
      return;
    }
    const trimmedAnswer =
      revealedWords.length > 0 ? revealedWords.join(' ') : String(guessInput ?? '').trim();
    if (!trimmedAnswer) {
      triggerShake();
      setMessage('Please guess at least one word before submitting.');
      return;
    }
    setShowSubmitConfirm(true);
  };

  const handleSubmit = async (e, { auto = false } = {}) => {
    if (e?.preventDefault) e.preventDefault();
    if (submitted) return;
    if (wordsRevealed) {
      if (!auto) setMessage('Wait for the words to hide before submitting.');
      return;
    }
    const trimmedAnswer =
      revealedWords.length > 0 ? revealedWords.join(' ') : String(guessInput ?? '').trim();
    if (!trimmedAnswer && !auto) {
      triggerShake();
      setMessage('Please guess at least one word before submitting.');
      return;
    }

    const guessDeadline = readGuessDeadline(user);
    const guessElapsed = resolveCountdownElapsedSeconds({
      totalSeconds: GUESS_SECONDS,
      secondsLeft: guessSecondsLeft,
      deadlineMs: guessDeadline,
    });
    const stageTimeSeconds = MEMORIZE_SECONDS + guessElapsed;

    clearIntervalTimer(guessTimerRef);
    clearGuessDeadline(user);

    const userId = user?.userId;
    const organizationId = user?.organizationId;
    const sessionId = user?.sessionId;
    const token =
      adminToken ||
      (sessionId && organizationId ? `${sessionId}&${organizationId}` : null);

    if (!token || !userId || !organizationId || !sessionId) {
      setMessage('Unable to save your response. Please sign in again.');
      return;
    }

    if (isDemoSubscribeRedirectUser(user)) {
      const guessedAnswer = revealedWords.length > 0 ? revealedWords.join(' ') : String(guessInput ?? '').trim();
      const correctCount = countCorrectWords(parseGuessWords(guessedAnswer), goodWords);
      const allCorrect = goodWords.length > 0 && correctCount === goodWords.length;
      const finalPoints = correctCount;
      runDemoStageSubmitFlow({
        user,
        navigate,
        revealTimeoutRef,
        nextPathRef,
        beginSubmit,
        markSubmitted: () => setSubmitted(true),
        setMessage,
        setRevealCompletedDays,
        setShowRevealOverlay,
        completedDaysForReveal: STAGE_NUMBER,
      });
      if (typeof onStageComplete === 'function' && !completeSentRef.current) {
        completeSentRef.current = true;
        onStageComplete({
          earnedPoints: finalPoints,
          answer: guessedAnswer,
          allCorrect,
        });
      }
      return;
    }

    const guessedAnswer = revealedWords.length > 0 ? revealedWords.join(' ') : String(guessInput ?? '').trim();
    const correctCount = countCorrectWords(parseGuessWords(guessedAnswer), goodWords);
    const allCorrect = goodWords.length > 0 && correctCount === goodWords.length;
    const finalPoints = correctCount;

    setMessage('');
    beginSubmit();
    setSubmitted(true);

    try {
      await initStageReport({
        token,
        userId,
        email: user?.email,
        name: user?.name,
        userType: user?.role || user?.source || 'ORG_USER',
        reportId: user?.reportId || '',
        themeName: 'Big Picture',
        totalPuzzles: themeData?.total_puzzles ?? 9,
        timeSeconds: stageTimeSeconds,
      });

      const stageData = await fetchGameStages({ token, userId, organizationId, sessionId });
      const game = stageData?.game ?? null;
      const nextDay = stageData?.nextDay != null ? Number(stageData.nextDay) : null;
      if (!Number.isFinite(nextDay) || nextDay < 1) {
        throw new Error('No active day found for this stage');
      }

      mergeGameAssignmentIntoUserData({
        game,
        currentStage: stageData?.currentStage,
        nextDay: nextDay + 1,
      });

      const reportTimes = buildCumulativeStageTimes(stageData, stageTimeSeconds);
      const reportScores = buildCumulativeStageScore(stageData, finalPoints);

      const progressResult = await updateStageProgress({
        token,
        userId,
        dayNumber: STAGE_NUMBER,
        points: finalPoints,
        timeSeconds: reportTimes.currentTimeSeconds,
        answer: guessedAnswer,
        allCorrect,
        email: user?.email,
        name: user?.name,
      });

      sendReport({
        token,
        userId,
        email: user?.email,
        points: readCumulativeScoreFromProgress(progressResult) ?? reportScores.cumulativeScore,
        accumulatePoints: false,
        time: readCumulativeTimeFromProgress(progressResult) ?? reportTimes.cumulativeTimeSeconds,
        name: user?.name,
        userType: user?.role || user?.source || 'ORG_USER',
        reportId: user?.reportId || '',
        themeName: 'Big Picture',
      });
    } catch (err) {
      setSubmitted(false);
      autoSubmitRef.current = false;
      setMessage(err?.message || 'Failed to save your response. Please try again.');
      return;
    }

    let path = '/puzzle';
    let completedDaysForReveal = 1;
    try {
      const { path: nextPath, completedDaysForReveal: revealDays } =
        await resolvePostSubmitNavigation({
          stageNumber: STAGE_NUMBER,
          token,
          userId,
          organizationId,
          sessionId,
        });
      path = nextPath;
      completedDaysForReveal = revealDays;
    } catch {
      path = '/puzzle';
    }

    if (typeof onStageComplete === 'function' && !completeSentRef.current) {
      completeSentRef.current = true;
      onStageComplete({
        earnedPoints: finalPoints,
        answer: trimmedAnswer,
        allCorrect,
      });
    }

    nextPathRef.current = path;
    setRevealCompletedDays(completedDaysForReveal);
    setShowRevealOverlay(true);
    scheduleStagePostSubmitNavigation({
      user,
      navigate,
      path,
      revealTimeoutRef,
    });
  };

  handleSubmitRef.current = handleSubmit;

  const confettiPieces = useMemo(
    () =>
      Array.from({ length: 22 }, (_, i) => ({
        id: i,
        left: `${Math.round((i / 21) * 100)}%`,
        delay: `${(i % 7) * 0.08}s`,
        duration: `${2.1 + (i % 5) * 0.22}s`,
      })),
    []
  );

  const handleRevealOverlayClick = (ev) => {
    if (ev.target !== ev.currentTarget) return;
    completeStageRevealNavigation({
      user,
      navigate,
      path: nextPathRef.current || '/puzzle',
      revealTimeoutRef,
    });
  };

  return (
    <div className="stage1-page" style={pageStyle}>
      <StagePlayGate
        playCheckReady={playCheckReady}
        alreadyPlayed={alreadyPlayed}
        isConfigured={isConfigured}
        notConfiguredMessage={
          <p className="main-day-subtitle">
            Stage 5 is not configured: set stage_5 title and at least one word in questions.
          </p>
        }
      >
        <>
          <div className="stage-page-main stage1-header-main">
            <header className="s1-top-bar">
              {memorizeReady && !showMemorizeIntro ? (
                <div className="s1-top-timer" aria-label="Stage timer" aria-live="polite">
                  <ClockIcon />
                  <span className="s1-remember-label">{phaseTimerLabel}</span>
                  <span className="s1-timer-value">{phaseTimerDisplay}</span>
                </div>
              ) : (
                <div className="s1-top-timer s1-top-timer--placeholder" aria-hidden />
              )}
              {rulesList.length > 0 ? (
                <button
                  type="button"
                  className="stage1-rules-fab"
                  onClick={handleOpenRulesClick}
                  aria-label="How to play"
                  title="How to play"
                >
                  ?
                </button>
              ) : null}
            </header>

            <header className="s1-hero-wrap">
              <div className="s1-hero">
                {logoUrl && !logoFailed ? (
                  <img
                    src={logoUrl}
                    alt={subtitle}
                    className="s1-hero-logo"
                    onError={() => setLogoFailed(true)}
                  />
                ) : (
                  <p className="s1-hero-logo-fallback">{subtitle}</p>
                )}
                <p className="s1-subtitle">{subtitle}</p>
                <span className="s1-hero-line" aria-hidden />
              </div>
            </header>
          </div>

          <div className="stage-page-layout s1-play-row">
            <div className="stage-page-main s1-play-main">
            <div className="stage1-card">
              <div className="stage1-words-panel">
                {memorizeReady ? (
                  <div className="stage1-words-grid" aria-label="Word list">
                    {wordEntries.map((entry, index) => {
                      const { word, type } = entry;
                      const revealed = wordsRevealed || revealedWordSet.has(word);
                      return (
                        <div
                          key={`word-${word}-${index}`}
                          className={`stage1-word-card ${revealed ? 'stage1-word-card--revealed' : 'stage1-word-card--hidden'}${type === 'bad' ? ' stage1-word-card--bad' : ''}`}
                          style={wordCardStyle(index)}
                          aria-label={revealed ? `Word: ${word}` : 'Hidden word card'}
                        >
                          <div className="stage1-word-card-inner">
                            <span className="stage1-word-card-face stage1-word-card-face--front">
                              {word}
                            </span>
                            <span className="stage1-word-card-face stage1-word-card-face--back">?</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="s1-study-footer">Loading words…</p>
                )}
              </div>

              {memorizeReady && wordsRevealed ? (
                <p className="s1-study-footer">
                  <i className="fa-regular fa-eye" aria-hidden />
                  There are {wordList.length} words. Study carefully!
                </p>
              ) : null}

              {memorizeReady && !wordsRevealed && !submitted && !showMemorizeIntro ? (
                <>
                  <p className="s1-study-footer">
                    <i className="fa-solid fa-pen" aria-hidden />
                    Words hidden — type one word and click OK to check it
                  </p>
                  <form
                    className="stage1-form"
                    onSubmit={(ev) => {
                      ev.preventDefault();
                    }}
                  >
                    <label className="stage1-label" htmlFor="stage1-response">
                      Guess a word
                    </label>
                    <div className="stage1-guess-row">
                      <input
                        ref={guessInputRef}
                        id="stage1-response"
                        className={`stage1-word-input ${inputShake ? 'stage1-textarea--shake' : ''} ${inputWrong ? 'stage1-word-input--wrong' : ''}`}
                        value={guessInput}
                        onChange={(ev) => {
                          setGuessInput(ev.target.value);
                          setInputWrong(false);
                          if (message) setMessage('');
                        }}
                        onKeyDown={(ev) => {
                          if (ev.key === 'Enter') {
                            ev.preventDefault();
                            handleWordGuess();
                          }
                        }}
                        placeholder="Type one word at a time and press OK"
                        disabled={submitted}
                        aria-describedby="stage1-char-count"
                      />
                      <button
                        type="button"
                        className="stage1-word-ok"
                        onClick={handleWordGuess}
                        disabled={submitted || String(guessInput ?? '').trim() === ''}
                      >
                        OK
                      </button>
                    </div>
                    <div id="stage1-char-count" className="stage1-char-count">
                      {revealedWords.length}/{goodWords.length} good words found
                    </div>
                    <button
                      type="button"
                      className="stage1-submit"
                      onClick={handleSubmitClick}
                      disabled={submitted || (revealedWords.length === 0 && String(guessInput ?? '').trim() === '')}
                    >
                      Submit
                    </button>
                  </form>
                </>
              ) : null}

              {submitted ? (
                <p className="stage1-complete">Thanks! Your response has been submitted.</p>
              ) : null}

              {message ? <div className="stage1-hint">{message}</div> : null}
            </div>
              </div>

          <aside
            className="stage-page-puzzle-aside s1-puzzle-panel"
            aria-label="Puzzle pieces progress"
          >
            <StagePuzzleReveal themeData={themeData} />
          </aside>
          </div>

          {showRevealOverlay ? (
            <div
              className="s1-reveal-overlay"
              role="dialog"
              aria-modal="true"
              aria-label="Revealing your puzzle progress"
              onClick={handleRevealOverlayClick}
            >
              <div className="s1-reveal-confetti" aria-hidden>
                {confettiPieces.map((piece) => (
                  <span
                    key={piece.id}
                    className="s1-reveal-confetti-piece"
                    style={{
                      left: piece.left,
                      animationDelay: piece.delay,
                      animationDuration: piece.duration,
                    }}
                  />
                ))}
              </div>
              <div className="s1-reveal-stage">
                <aside
                  className="s1-puzzle-panel s1-puzzle-panel--reveal"
                  aria-label="Puzzle pieces progress"
                >
                  <StagePuzzleReveal
                    themeData={themeData}
                    completedDaysOverride={revealCompletedDays}
                    animateReveal
                  />
                </aside>
              </div>
            </div>
          ) : null}
        </>
      </StagePlayGate>

      {showMemorizeIntro ? (
        <div
          className="stage1-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="stage1-memorize-intro-title"
        >
          <div className="stage1-modal" onClick={(ev) => ev.stopPropagation()}>
            <h2 id="stage1-memorize-intro-title">Get ready</h2>
            <p className="stage1-submit-confirm-text">
              You have {MEMORIZE_SECONDS} seconds to remember all the words. Your time has started.
            </p>
            <div className="stage1-modal-actions">
              <button type="button" className="stage1-modal-btn" onClick={handleMemorizeIntroConfirm}>
                OK
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {showBadWordModal ? (
        <div
          className="stage1-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="stage1-bad-word-title"
          onClick={() => setShowBadWordModal(false)}
        >
          <div className="stage1-modal" onClick={(ev) => ev.stopPropagation()}>
            <h2 id="stage1-bad-word-title">Negative</h2>
            <p className="stage1-submit-confirm-text">
              <strong>{badWordGuess}</strong> isn&apos;t a positive behavior. Let&apos;s try others.
            </p>
            <div className="stage1-modal-actions">
              <button
                type="button"
                className="stage1-modal-btn"
                onClick={() => setShowBadWordModal(false)}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {showSubmitConfirm ? (
        <div
          className="stage1-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="stage1-submit-confirm-title"
          onClick={() => setShowSubmitConfirm(false)}
        >
          <div className="stage1-modal" onClick={(ev) => ev.stopPropagation()}>
            <h2 id="stage1-submit-confirm-title">Submit your answer?</h2>
            <p className="stage1-submit-confirm-text">
              You won&apos;t be able to change your guess after submitting.
            </p>
            <div className="stage1-modal-actions stage1-modal-actions--split">
              <button
                type="button"
                className="stage1-modal-btn stage1-modal-btn--secondary"
                onClick={() => setShowSubmitConfirm(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="stage1-modal-btn"
                onClick={() => {
                  setShowSubmitConfirm(false);
                  handleSubmit();
                }}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {showRules && rulesList.length > 0 ? (
        <div
          className="stage1-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="stage1-rules-title"
          onClick={closeRules}
        >
          <div className="stage1-modal" onClick={(ev) => ev.stopPropagation()}>
            <h2 id="stage1-rules-title">How to play</h2>
            <ul className="stage1-rules-list">
              {rulesList.map((line, i) => (
                <li key={i}>{line}</li>
              ))}
            </ul>
            <div className="stage1-modal-actions">
              <button type="button" onClick={closeRules}>
                Close
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
